import { type MouseEvent, useEffect } from "react"
import "./RoomlistFilters.scss"
import {
    isSpyPriceFiltersActive,
    shouldShowHomepageFilters,
    ShowType,
} from "@multimediallc/cb-roomlist-prefetch"
import { error } from "@multimediallc/logging"
import {
    UrlStateContextProvider,
    useUrlStateContext,
} from "../../../../../hooks/urlStateContext"
import { CombinedOverlay } from "../CombinedOverlay"
import { FiltersContextProvider, useFiltersContext } from "../FiltersContext"
import { FiltersDrawer } from "../FiltersDrawer"
import { AgeSection } from "../filterSections/AgeSection"
import { HashtagSection } from "../filterSections/hashtagSection/HashtagSection"
import { LanguageSection } from "../filterSections/languageSection/LanguageSection"
import { PrivatePriceSection } from "../filterSections/PrivatePriceSection"
import { RegionSection } from "../filterSections/RegionSection"
import { RoomSizeSection } from "../filterSections/RoomSizeSection"
import { SpyPriceSection } from "../filterSections/SpyPriceSection/SpyPriceSection"
import { MobileFilterNav } from "../MobileFilterNav"
import { ActiveFiltersSection } from "../types"
import type { EventRouter } from "../../../../messaging/types"

const PLACEHOLDER_ID = "mobile-filter-nav-placeholder"
const PLACEHOLDER_HIDE_CLASS = "mobile-filter-nav-placeholder--hidden"
const HTML_FILTERS_NAV_CLASS = "filters-nav-shown"

export interface IRoomlistFiltersProps {
    showLocationRouter: EventRouter<boolean>
    setThumbnailAnimations: (animate: boolean) => void
    reloadRooms: (onSuccess: () => void) => void
}

export function RoomlistFilters(props: IRoomlistFiltersProps) {
    return (
        <UrlStateContextProvider>
            <FiltersContextProvider>
                <RoomlistFiltersContent
                    setThumbnailAnimations={props.setThumbnailAnimations}
                    showLocationRouter={props.showLocationRouter}
                    reloadRooms={props.reloadRooms}
                />
            </FiltersContextProvider>
        </UrlStateContextProvider>
    )
}

const updatePlaceholderVisibility = (currentShowType?: ShowType) => {
    const placeholderElement = document.getElementById(PLACEHOLDER_ID)
    if (!placeholderElement) {
        error(
            `[RoomlistFiltersContent] Element with ID "${PLACEHOLDER_ID}" not found.`,
        )
        return
    }
    if (currentShowType === ShowType.FOLLOW_OFFLINE) {
        placeholderElement.classList.add(PLACEHOLDER_HIDE_CLASS)
    } else {
        placeholderElement.classList.remove(PLACEHOLDER_HIDE_CLASS)
    }
}

const updateHtmlFiltersClass = () => {
    const htmlElement = document.documentElement
    if (shouldShowHomepageFilters()) {
        htmlElement.classList.add(HTML_FILTERS_NAV_CLASS)
    } else {
        htmlElement.classList.remove(HTML_FILTERS_NAV_CLASS)
    }
}

export function RoomlistFiltersContent(props: IRoomlistFiltersProps) {
    const { overlayedSection, setOverlayedSection } = useFiltersContext()
    const urlStateContext = useUrlStateContext()

    useEffect(() => {
        updatePlaceholderVisibility(urlStateContext.showType)
    }, [urlStateContext.showType])

    useEffect(() => {
        updateHtmlFiltersClass()
    }, [urlStateContext.pageType, urlStateContext.showType]) // these 2 variables determine if the filters nav should be shown

    useEffect(() => {
        const canShowSpyPrices =
            urlStateContext.spyShowPrices &&
            isSpyPriceFiltersActive() &&
            urlStateContext.spyShowPrices.length > 0
        const canShowPrivatePrices =
            urlStateContext.privatePrices &&
            urlStateContext.privatePrices.length > 0

        // If PrivatePrices filter is open on Spy Page, switch to SpyPrices or None
        if (
            overlayedSection === ActiveFiltersSection.PrivatePrices &&
            urlStateContext.showType === ShowType.PRIVATE
        ) {
            if (canShowSpyPrices) {
                setOverlayedSection(ActiveFiltersSection.SpyPrices)
            } else {
                setOverlayedSection(ActiveFiltersSection.None)
            }
        }
        // If SpyPrices filter is open on a Non-Spy Page, switch to PrivatePrices or None
        else if (
            overlayedSection === ActiveFiltersSection.SpyPrices &&
            urlStateContext.showType !== ShowType.PRIVATE
        ) {
            if (canShowPrivatePrices) {
                setOverlayedSection(ActiveFiltersSection.PrivatePrices)
            } else {
                setOverlayedSection(ActiveFiltersSection.None)
            }
        }
    }, [
        overlayedSection,
        setOverlayedSection,
        urlStateContext.showType,
        urlStateContext.spyShowPrices,
        urlStateContext.privatePrices,
    ])

    if (!shouldShowHomepageFilters()) {
        return null
    }

    return (
        <div
            className="RoomlistFilters"
            data-testid="roomlist-filters-component"
        >
            <MobileFilterNav />
            {overlayedSection !== ActiveFiltersSection.None && (
                <div
                    className="RoomlistFilters__Overlay"
                    data-testid="roomlist-filters-overlay"
                    onClick={(e: MouseEvent<HTMLDivElement>) => {
                        e.stopPropagation()
                        e.preventDefault()
                        setOverlayedSection(ActiveFiltersSection.None)
                    }}
                />
            )}
            <FiltersDrawer
                hidden={overlayedSection === ActiveFiltersSection.None}
                onHideSection={() =>
                    setOverlayedSection(ActiveFiltersSection.None)
                }
                type={overlayedSection}
            >
                {overlayedSection === ActiveFiltersSection.Regions && (
                    <RegionSection />
                )}
                {overlayedSection === ActiveFiltersSection.RoomSize && (
                    <RoomSizeSection />
                )}
                {overlayedSection === ActiveFiltersSection.Tags && (
                    <HashtagSection />
                )}
                {overlayedSection === ActiveFiltersSection.PrivatePrices && (
                    <PrivatePriceSection />
                )}
                {overlayedSection === ActiveFiltersSection.SpyPrices &&
                    isSpyPriceFiltersActive() && <SpyPriceSection />}
                {overlayedSection === ActiveFiltersSection.Ages && (
                    <AgeSection />
                )}
                {overlayedSection === ActiveFiltersSection.Languages && (
                    <LanguageSection />
                )}
                {overlayedSection === ActiveFiltersSection.Combined && (
                    <CombinedOverlay
                        showLocationRouter={props.showLocationRouter}
                        setThumbnailAnimations={props.setThumbnailAnimations}
                        reloadRooms={props.reloadRooms}
                    />
                )}
            </FiltersDrawer>
        </div>
    )
}
